import type { Ref } from 'vue'

import { resetCacheOnStaleVersion } from '~~/shared/utils/staleVersionCache'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config, PendingRemoval, PlayLike } from '../@types/editor'

import { createPlayKey, markQuickPicked } from './editorPlay'

type PlaysStateOptions<TPlay> = {
  createPlay: (config: Config, index: number) => TPlay
  isPlayComplete: (config: Config, play: TPlay) => boolean
  isPlayDirty?: (play: TPlay) => boolean
  isPlayValid?: (config: Config, play: TPlay) => boolean
  lotteryId: LotteryId
  quickPickPlay: (config: Config, play: TPlay) => void
  reconcilePlay?: (config: Config, play: TPlay) => void
}

const DEFAULT_MAX_PLAYS = 1

export const createPlaysState = <
  TPlay extends PlayLike & { id: string; key: string; quickPick?: string },
>({
  createPlay,
  isPlayComplete,
  isPlayDirty = () => false,
  isPlayValid = () => true,
  lotteryId,
  quickPickPlay,
  reconcilePlay,
}: PlaysStateOptions<TPlay>) => {
  const configVersion = ref<Config['version']>('')
  const expandedPlayId = ref<Maybe<string>>(null)
  const pendingRemovals = ref<PendingRemoval<TPlay>[]>([]) as Ref<PendingRemoval<TPlay>[]>
  const playSequence = ref(0)
  const plays = ref<TPlay[]>([]) as Ref<TPlay[]>
  const removalSequence = ref(0)
  // plays from an earlier session wait here until the customer restores or drops them
  const savedPlays = ref<TPlay[]>([]) as Ref<TPlay[]>

  const getPlay = (playId: string) => plays.value.find((play) => play.id === playId)

  const isPlayEmpty = (play: TPlay) =>
    !isPlayDirty(play) && play.fields.every((field) => !field.selected.length)

  const isPlayFlagged = (config: Config, play: TPlay) =>
    !isPlayEmpty(play) && (!isPlayComplete(config, play) || !isPlayValid(config, play))

  const maxPlays = (config: Config) => config.allowedPlays.at(-1) ?? DEFAULT_MAX_PLAYS

  const emptyPlays = () => plays.value.filter((play) => isPlayEmpty(play))

  const usedPlaySlots = () => plays.value.length + pendingRemovals.value.length

  // empty plays are scratchpads and do not count towards the max plays limit
  const remainingPlaySlots = (config: Config) =>
    Math.max(0, maxPlays(config) - usedPlaySlots() + emptyPlays().length)

  const hasFreePlaySlot = (config: Config) => usedPlaySlots() < maxPlays(config)

  const isQuickPickFillTarget = (config: Config) => {
    const [firstPlay] = plays.value
    return plays.value.length === 1 && !!firstPlay && !isPlayComplete(config, firstPlay)
  }

  const remainingQuickPickSlots = (config: Config) => {
    const [firstPlay] = plays.value
    const fillsIncompletePlay =
      isQuickPickFillTarget(config) && !!firstPlay && !isPlayEmpty(firstPlay)
    return remainingPlaySlots(config) + (fillsIncompletePlay ? 1 : 0)
  }

  const createNextPlay = (config: Config) => {
    const play = createPlay(config, playSequence.value)
    playSequence.value += 1
    return play
  }

  const addPlay = (config: Config): Maybe<string> => {
    const emptyPlay = plays.value.length > 1 ? plays.value.find(isPlayEmpty) : undefined
    if (emptyPlay) {
      if (plays.value.at(-1) !== emptyPlay) {
        plays.value = [...plays.value.filter((play) => play !== emptyPlay), emptyPlay]
      }
      return emptyPlay.id
    }

    if (!hasFreePlaySlot(config)) return null

    const play = createNextPlay(config)
    plays.value.push(play)
    return play.id
  }

  const discardPlay = (playId: string) => {
    const playsIndex = plays.value.findIndex((play) => play.id === playId)
    if (playsIndex < 0 || plays.value.length <= 1) return

    plays.value.splice(playsIndex, 1)
    if (expandedPlayId.value === playId) expandedPlayId.value = null
  }

  const quickPickPlays = (
    config: Config,
    quantity: number,
    fill: (play: TPlay) => boolean = (play) => {
      quickPickPlay(config, play)
      return true
    },
  ): string[] => {
    let remaining = quantity
    const playIds: string[] = []
    const disposable = isQuickPickFillTarget(config) ? [] : emptyPlays()

    if (isQuickPickFillTarget(config)) {
      const [firstPlay] = plays.value
      if (!fill(firstPlay)) return []
      markQuickPicked(firstPlay)
      playIds.push(firstPlay.id)
      remaining -= 1
    }

    while (remaining > 0 && remainingPlaySlots(config)) {
      const play = createNextPlay(config)
      if (!fill(play)) break
      markQuickPicked(play)
      plays.value.push(play)
      disposable.splice(0).forEach(({ id }) => discardPlay(id))
      playIds.push(play.id)
      remaining -= 1
    }

    if (!playIds.length) return []

    expandedPlayId.value = null
    return playIds
  }

  const quickPick = (playId: string, config: Config) => {
    const play = getPlay(playId)
    if (!play) return
    quickPickPlay(config, play)
    markQuickPicked(play)
  }

  const toCombinedIndex = (playsIndex: number) =>
    pendingRemovals.value.reduce(
      (combined, { index }) => (index <= combined ? combined + 1 : combined),
      playsIndex,
    )

  const toPlaysIndex = (combinedIndex: number, removalId: string) =>
    combinedIndex -
    pendingRemovals.value.filter(({ id, index }) => id !== removalId && index < combinedIndex)
      .length

  const removePlay = (playId: string, height: Maybe<number>): Maybe<string> => {
    const playsIndex = plays.value.findIndex((play) => play.id === playId)
    if (playsIndex < 0) return null

    const index = toCombinedIndex(playsIndex)
    const [play] = plays.value.splice(playsIndex, 1)
    removalSequence.value += 1
    const removalId = `removal-${removalSequence.value}`
    pendingRemovals.value = [...pendingRemovals.value, { height, id: removalId, index, play }].sort(
      (a, b) => a.index - b.index,
    )

    if (expandedPlayId.value === playId) expandedPlayId.value = null

    return removalId
  }

  const dropRemoval = (removalId: string) => {
    pendingRemovals.value = pendingRemovals.value.filter(({ id }) => id !== removalId)
  }

  const expireRemoval = (removalId: string, config: Config) => {
    const removal = pendingRemovals.value.find(({ id }) => id === removalId)
    dropRemoval(removalId)

    if (removal) {
      pendingRemovals.value = pendingRemovals.value.map((pending) =>
        pending.index > removal.index ? { ...pending, index: pending.index - 1 } : pending,
      )
    }

    if (plays.value.length || pendingRemovals.value.length) return

    const play = createNextPlay(config)
    plays.value = [play]
    expandedPlayId.value = play.id
  }

  const restorePlay = (removalId: string): Maybe<string> => {
    const removal = pendingRemovals.value.find(({ id }) => id === removalId)
    if (!removal) return null

    const playsIndex = Math.min(
      Math.max(toPlaysIndex(removal.index, removalId), 0),
      plays.value.length,
    )
    plays.value.splice(playsIndex, 0, removal.play)
    dropRemoval(removalId)
    if (isPlayEmpty(removal.play)) removeEmptyPlaysExcept(removal.play.id)
    expandedPlayId.value = null

    return removal.play.id
  }

  const resetPlays = () => {
    pendingRemovals.value = []
    plays.value = []
    playSequence.value = 0
    removalSequence.value = 0
    expandedPlayId.value = null
  }

  const stashPlays = () => {
    const sequence = playSequence.value
    savedPlays.value = plays.value.filter((play) => !isPlayEmpty(play))
    resetPlays()
    // the saved plays keep their ids, new plays must not reuse them
    playSequence.value = sequence
  }

  const clearSavedPlays = () => {
    savedPlays.value = []
  }

  const clear = () => {
    resetPlays()
    clearSavedPlays()
  }

  const restoreSavedPlays = (config: Config) => {
    plays.value = savedPlays.value
    savedPlays.value = []
    alignPlays(config)
    plays.value.forEach((play) => (play.key ||= createPlayKey()))
    playSequence.value = Math.max(playSequence.value, plays.value.length)
    expandedPlayId.value = null
  }

  const deleteAllPlays = (config: Config) => {
    resetPlays()

    const play = createNextPlay(config)
    plays.value = [play]
    expandedPlayId.value = play.id
  }

  const alignPlay = (config: Config, play: TPlay) => {
    play.fields = config.numbers.map(({ name, selectableMax, selectableNumbers }) => {
      const selected = play.fields.find((field) => field?.name === name)?.selected ?? []

      return {
        name,
        selected: selected
          .filter((value) => selectableNumbers.includes(value))
          .slice(0, selectableMax),
      }
    })

    reconcilePlay?.(config, play)
  }

  const alignPlays = (config: Config) => {
    pendingRemovals.value = []
    plays.value = plays.value.slice(0, maxPlays(config))
    plays.value.forEach((play) => alignPlay(config, play))
  }

  const removeEmptyPlaysExcept = (playId: string) => {
    plays.value = plays.value.filter((play) => play.id === playId || !isPlayEmpty(play))
  }

  // the server rendered one empty play, an empty play under a new key would slide in after hydration
  const adoptPlayKey = (key: string) => {
    const [play] = plays.value
    if (plays.value.length === 1 && play && isPlayEmpty(play)) play.key = key
  }

  const setExpandedPlay = (playId: Maybe<string>) => {
    const collapsedId = expandedPlayId.value
    expandedPlayId.value = playId
    if (!collapsedId || collapsedId === playId) return

    const collapsed = getPlay(collapsedId)
    if (collapsed && isPlayEmpty(collapsed)) discardPlay(collapsedId)
  }

  const setUp = (config: Config) => {
    const wasReset = resetCacheOnStaleVersion({
      cache: 'plays',
      cachedVersion: configVersion.value,
      freshVersion: config.version,
      hasSelection: () => [...plays.value, ...savedPlays.value].some((play) => !isPlayEmpty(play)),
      log: (type, payload) => useDatadog().log(type, payload),
      productId: lotteryId,
      reset: clear,
      syncVersion: (version) => (configVersion.value = version),
    })

    if (!wasReset) alignPlays(config)

    plays.value.forEach((play) => (play.key ||= createPlayKey()))

    playSequence.value = Math.max(playSequence.value, plays.value.length)

    if (!plays.value.length) plays.value = [createNextPlay(config)]

    expandedPlayId.value = plays.value.find((play) => !isPlayComplete(config, play))?.id ?? null
  }

  return {
    addPlay,
    adoptPlayKey,
    clear,
    clearSavedPlays,
    configVersion,
    deleteAllPlays,
    discardPlay,
    expandedPlayId,
    expireRemoval,
    getPlay,
    isPlayEmpty,
    isPlayFlagged,
    isPlayValid,
    isQuickPickFillTarget,
    maxPlays,
    pendingRemovals,
    plays,
    playSequence,
    quickPick,
    quickPickPlays,
    remainingPlaySlots,
    remainingQuickPickSlots,
    removeEmptyPlaysExcept,
    removePlay,
    resetPlays,
    restorePlay,
    restoreSavedPlays,
    savedPlays,
    setExpandedPlay,
    setUp,
    stashPlays,
  }
}

export const createLotteryStoreCache = <TUseStore extends () => unknown>(
  create: (lotteryId: LotteryId) => TUseStore,
) => {
  const cache = new Map<LotteryId, TUseStore>()

  return (lotteryId: LotteryId) => {
    const useStore = cache.get(lotteryId) ?? create(lotteryId)
    cache.set(lotteryId, useStore)
    return useStore() as ReturnType<TUseStore>
  }
}
